import type { Edge } from "@xyflow/react";
import type { NoxusReactflowNodeV2 } from "@/types/spotflow-v2";

/**
 * Transient scratch state owned by the history-recording paths in
 * `actions/edges.ts`. Lives here (instead of as floating module-level
 * `let`s scattered across that file) so it's:
 *   - easy to reset between tests via `resetHistoryRuntime()`,
 *   - one place to look when debugging stuck/leaking state,
 *   - one place to swap if we ever scope per-store / per-editor.
 *
 * Today it's still a process-global singleton — a single editor instance is
 * the only consumer.
 */

export interface DeleteBuffer {
  nodesBefore: ReadonlyArray<NoxusReactflowNodeV2>;
  edgesBefore: ReadonlyArray<Edge>;
  removedNodeIds: Set<string>;
  removedEdgeIds: Set<string>;
}

export interface PositionSnapshot {
  x: number;
  y: number;
}

export interface ResizeSnapshot {
  width: number | undefined;
  height: number | undefined;
  style: NoxusReactflowNodeV2["style"] | undefined;
}

interface HistoryRuntime {
  pendingDelete: DeleteBuffer | null;
  dragSnapshot: Map<string, PositionSnapshot> | null;
  resizeSnapshots: Map<string, ResizeSnapshot> | null;
}

export const historyRuntime: HistoryRuntime = {
  pendingDelete: null,
  dragSnapshot: null,
  resizeSnapshots: null,
};

export function resetHistoryRuntime(): void {
  historyRuntime.pendingDelete = null;
  historyRuntime.dragSnapshot = null;
  historyRuntime.resizeSnapshots = null;
}
